/* tslint:disable */
/* eslint-disable */
/**
 * Scrivio Invoices API
 * API for managing PDF invoices in the Brief (manual/particulier) billing flow.  Invoices are generated for non-Vecozo billing scenarios where clients pay directly, or where invoices are sent to factoring companies. Each invoice contains one or more billing lines from the same client. 
 *
 * The version of the OpenAPI document: 1.0.0
 * 
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import type { Configuration } from './configuration';
import type { AxiosPromise, AxiosInstance, RawAxiosRequestConfig } from 'axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import { DUMMY_BASE_URL, assertParamExists, setApiKeyToObject, setBasicAuthToObject, setBearerAuthToObject, setOAuthToObject, setSearchParams, serializeDataIfNeeded, toPathString, createRequestFunction } from './common';
import type { RequestArgs } from './base';
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, BaseAPI, RequiredError, operationServerMap } from './base';

export interface BatchPdfRequest {
    /**
     * Invoice IDs to include in the batch PDF
     */
    'invoiceIds': Array<string>;
}
export interface CreateInvoiceRequest {
    /**
     * Client to invoice
     */
    'clientId': string;
    /**
     * Billing lines to include on the invoice
     */
    'billingLineIds': Array<string>;
    /**
     * Invoice date (defaults to today)
     */
    'invoiceDate'?: string;
    /**
     * Number of days until payment is due (vervaldatum = invoiceDate + dueDays). 0 means the invoice is due on the invoice date itself (\"betaal direct\"). When omitted, the organisation\'s configured default betaaltermijn is used, falling back to 30 days when the organisation has none configured (#2949). The bound matches the organisation default\'s range (#3418). 
     */
    'dueDays'?: number;
    /**
     * BTW percentage to apply (0 for healthcare exemption, 21 for standard)
     */
    'btwPercentage'?: number;
}
export interface CreateInvoicesFromBillingLinesRequest {
    /**
     * Billing lines to create invoices from. Lines will be grouped by client, creating one invoice per client. All billing lines must be in READY status and not already on another invoice. 
     */
    'billingLineIds': Array<string>;
    /**
     * Invoice date (defaults to today)
     */
    'invoiceDate'?: string;
    /**
     * Number of days until payment is due (vervaldatum = invoiceDate + dueDays). 0 means the invoice is due on the invoice date itself (\"betaal direct\"). When omitted, the organisation\'s configured default betaaltermijn is used, falling back to 30 days when the organisation has none configured (#2949). The bound matches the organisation default\'s range (#3418). 
     */
    'dueDays'?: number;
    /**
     * BTW percentage to apply (0 for healthcare exemption, 21 for standard)
     */
    'btwPercentage'?: number;
}
export interface CreateInvoicesFromBillingLinesResponse {
    /**
     * List of created invoices (one per client)
     */
    'invoices': Array<InvoiceResponse>;
}
export interface DebiteurPeriodeRequest {
    'debiteurId': string;
    'geldigVanaf': string;
    /**
     * Inclusive; absent or null for an open-ended period
     */
    'geldigTot'?: string | null;
}
export interface DebiteurPeriodeResponse {
    'id': string;
    'zorgtrajectId': string;
    'debiteurId': string;
    'geldigVanaf': string;
    'geldigTot'?: string | null;
}
/**
 * Every field of a debiteur; an update rewrites all of them.
 */
export interface DebiteurRequest {
    'naam': string;
    'contactpersoon'?: string | null;
    'adresStraat'?: string | null;
    'adresPostcode'?: string | null;
    'adresPlaats'?: string | null;
    /**
     * KvK-nummer. Separators are dropped (\"KvK 1234 5678\" is stored as 12345678); what is left must be eight digits.
     */
    'kvkNumber'?: string | null;
    /**
     * Where this party wants its invoices
     */
    'factuurEmail'?: string | null;
    /**
     * The practice\'s own reference at this party, printed on every invoice to it
     */
    'uwKenmerk'?: string | null;
    /**
     * Deactivated debiteuren are left out of the pickers
     */
    'actief': boolean;
}
export interface DebiteurResponse {
    'id': string;
    'naam': string;
    'contactpersoon'?: string | null;
    'adresStraat'?: string | null;
    'adresPostcode'?: string | null;
    'adresPlaats'?: string | null;
    'kvkNumber'?: string | null;
    'factuurEmail'?: string | null;
    'uwKenmerk'?: string | null;
    'actief': boolean;
    'createdAt': number;
    'updatedAt': number;
}
export interface InvoiceDetailResponse {
    'invoice': InvoiceResponse;
    /**
     * Invoice line items
     */
    'lines': Array<InvoiceLineResponse>;
}
/**
 * Why the stored document exists, and therefore what it is evidence of: - SENT: rendered when the invoice was issued. This is the document the client   received, and the only one an insurer dispute can be reconstructed from. - RECONSTRUCTED: rendered after the fact for an invoice that was issued before its   PDF was stored. It is the best evidence available, not the document that was sent —   the care trajectory and the insurance policy are resolved by today\'s date, so it can   legitimately differ from what the client received. - REGENERATED: rendered by an explicit regenerate action on an invoice that already   had a stored document. It is a later version of the same document, not a stand-in   for a missing one, and it says nothing about what the client received.  The origin is set when the document is created and never flips: a document rendered after the fact cannot become the one the client received.  Only the first version of a chain can be SENT or RECONSTRUCTED, and every later version is REGENERATED. `InvoiceResponse.document` therefore carries REGENERATED exactly when the invoice publishes a later version — which a republish is what causes (#3986), and a regeneration on its own never does. 
 */

export const InvoiceDocumentOrigin = {
    Sent: 'SENT',
    Reconstructed: 'RECONSTRUCTED',
    Regenerated: 'REGENERATED'
} as const;

export type InvoiceDocumentOrigin = typeof InvoiceDocumentOrigin[keyof typeof InvoiceDocumentOrigin];


/**
 * The stored PDF of an invoice, as a reference to the dossier document holding it. The document hangs off the invoice, not the reverse.
 */
export interface InvoiceDocumentReference {
    /**
     * The dossier document holding the PDF. It lives in the client\'s dossier, so portal exposure, acknowledgment and client-facing download are answered there rather than on the invoice.
     */
    'documentId': string;
    /**
     * When the PDF was rendered (epoch milliseconds)
     */
    'generatedAt': number;
    'origin': InvoiceDocumentOrigin;
}


/**
 * One stored version of an invoice\'s PDF. Version 1 is what the invoice references and what the client portal serves until a republish moves it (#3986); a later version exists only where someone regenerated (#3967).  Deliberately not `InvoiceDocumentReference`: that one is the invoice\'s *pointer* at the document it was issued with, and answers `InvoiceResponse` — three fields, no author and no reason, because version 1 needs neither. This answers \"which versions exist and who made each\", so it carries the position in the chain and who stated what. Widening the reference to cover both would put four fields nothing reads on every invoice response.
 */
export interface InvoiceDocumentVersion {
    /**
     * The dossier document holding this version\'s bytes
     */
    'documentId': string;
    /**
     * Position in the chain, 1-based. Version 1 is the invoice\'s own document.
     */
    'version': number;
    'origin': InvoiceDocumentOrigin;
    /**
     * When this version was rendered (epoch milliseconds)
     */
    'generatedAt': number;
    /**
     * The user whose action produced this version
     */
    'generatedById': string;
    /**
     * Display name of that user
     */
    'generatedByName': string;
    /**
     * Why this version was regenerated, as the behandelaar stated it. Null for version 1, which needs no reason — it exists because the invoice was issued.
     */
    'reason'?: string | null;
}


export interface InvoiceLineResponse {
    /**
     * Invoice line unique identifier
     */
    'id': string;
    /**
     * Invoice ID
     */
    'invoiceId': string;
    /**
     * Source billing line ID
     */
    'billingLineId'?: string | null;
    /**
     * Date of service
     */
    'serviceDate': string;
    /**
     * Service description
     */
    'description': string;
    /**
     * Duration in minutes
     */
    'durationMinutes'?: number | null;
    /**
     * Quantity
     */
    'quantity': number;
    /**
     * Unit price
     */
    'unitPriceEuro': number;
    /**
     * Line total (contracted amount, drives Vecozo declarations)
     */
    'amountEuro': number;
    /**
     * NZa-based line amount (100% NZa max, #2405). Null for legacy lines with no NZa snapshot. Reference figure only: what the line charges is chargedAmountEuro, which resolves this against the practice\'s eigen tarief.
     */
    'nzaAmountEuro'?: number | null;
    /**
     * The unit tariff this line charges — the \"Tarief\" column of the nota. The 100% NZa maximum for a contracted line (the contract percentage is what the insurer reimburses, not what is charged, #2405), the practice\'s own tariff where it set one (#4392), and unitPriceEuro for a legacy line with no NZa snapshot.
     */
    'chargedTariffEuro': number;
    /**
     * The line total this line charges — the \"Bedrag\" column of the nota, resolved as chargedTariffEuro is. The invoice\'s subtotal, BTW and total are the sum of this across its lines (#4525), so a client rendering the nota\'s figures reads this and makes no pricing decision of its own.
     */
    'chargedAmountEuro': number;
    /**
     * Display order
     */
    'sortOrder': number;
    /**
     * Creation timestamp
     */
    'createdAt': number;
}
export interface InvoiceListResponse {
    /**
     * List of invoices for current page
     */
    'content': Array<InvoiceResponse>;
    /**
     * Current page number (0-indexed)
     */
    'page': number;
    /**
     * Number of items per page
     */
    'size': number;
    /**
     * Total number of invoices
     */
    'totalElements': number;
    /**
     * Total number of pages
     */
    'totalPages': number;
    /**
     * One entry per status bucket over every invoice the other filters (client, period, search) select — the status filter itself deliberately does not narrow it, so the status tabs keep counting the whole selection while one of them is active. A bucket with no invoices is still returned, with zeroes.
     */
    'summary': Array<InvoiceStatusSummary>;
}
export interface InvoiceResponse {
    /**
     * Invoice unique identifier
     */
    'id': string;
    /**
     * Organization ID
     */
    'organizationId': string;
    /**
     * Invoice number (e.g. \"2026-0001\"). Null until the invoice is issued: the gapless number is assigned only on the DRAFT -> SENT transition, so an unsent draft has no number.
     */
    'invoiceNumber'?: string | null;
    /**
     * Invoice date
     */
    'invoiceDate': string;
    /**
     * Payment due date
     */
    'dueDate': string;
    /**
     * Client ID
     */
    'clientId': string;
    /**
     * Client display name
     */
    'clientName'?: string;
    /**
     * The debiteur this invoice is addressed to instead of the cliënt (#4750), or null when it is addressed to the cliënt. clientId still names whom the invoice concerns.
     */
    'debiteurId'?: string | null;
    /**
     * Subtotal before BTW
     */
    'subtotalEuro': number;
    /**
     * BTW percentage
     */
    'btwPercentage': number;
    /**
     * BTW amount
     */
    'btwEuro': number;
    /**
     * Total amount including BTW
     */
    'totalEuro': number;
    /**
     * Amount already paid
     */
    'paidEuro': number;
    /**
     * Outstanding balance
     */
    'outstandingEuro'?: number;
    'status': InvoiceStatus;
    /**
     * Payment reference
     */
    'paymentReference'?: string | null;
    /**
     * The practice\'s betaallink for this invoice (an absolute https URL), or null. Set through updateInvoicePaymentLink.
     */
    'paymentLink'?: string | null;
    /**
     * Notes
     */
    'notes'?: string | null;
    /**
     * When the invoice was sent
     */
    'sentAt'?: number | null;
    /**
     * When the invoice was fully paid
     */
    'paidAt'?: number | null;
    /**
     * Whether this invoice is a credit note (negative totals reversing originalInvoiceId)
     */
    'isCredit'?: boolean;
    /**
     * The issued invoice this credit note reverses (set only when isCredit is true)
     */
    'originalInvoiceId'?: string | null;
    'document'?: InvoiceDocumentReference;
    /**
     * Creation timestamp
     */
    'createdAt': number;
    /**
     * Last update timestamp
     */
    'updatedAt': number;
}


/**
 * Invoice lifecycle status: - DRAFT: Initial state, invoice is being prepared - SENT: Invoice has been finalized and sent to client - PAID: Invoice has been fully paid - OVERDUE: Payment is overdue - CANCELLED: Invoice was cancelled/voided 
 */

export const InvoiceStatus = {
    Draft: 'DRAFT',
    Sent: 'SENT',
    Paid: 'PAID',
    Overdue: 'OVERDUE',
    Cancelled: 'CANCELLED'
} as const;

export type InvoiceStatus = typeof InvoiceStatus[keyof typeof InvoiceStatus];


/**
 * What one status bucket holds across the whole selection, so a paged list can state totals it cannot see. Buckets are disjoint (see the `status` query parameter), so summing them gives the unfiltered selection.
 */
export interface InvoiceStatusSummary {
    'status': InvoiceStatus;
    /**
     * Number of invoices in this bucket
     */
    'count': number;
    /**
     * Sum of the invoice totals in this bucket
     */
    'totalEuro': number;
}


/**
 * RFC 7807 Problem Details for HTTP APIs
 */
export interface ProblemDetail {
    /**
     * URI reference identifying the problem type
     */
    'type': string;
    /**
     * Short, human-readable summary
     */
    'title': string;
    /**
     * HTTP status code
     */
    'status': number;
    /**
     * Human-readable explanation
     */
    'detail'?: string;
    /**
     * URI reference identifying the specific occurrence
     */
    'instance'?: string;
    /**
     * List of validation errors
     */
    'errors'?: Array<ProblemDetailErrorsInner>;
}
export interface ProblemDetailErrorsInner {
    /**
     * Field name that failed validation
     */
    'field'?: string;
    /**
     * Error message
     */
    'message'?: string;
}
export interface RecordPaymentRequest {
    /**
     * Payment amount in euros
     */
    'amount': number;
}
export interface RegenerateInvoicePdfRequest {
    /**
     * Why this invoice is being regenerated. Required, and kept on the new version — a version nobody can explain is what an append-only chain exists to avoid.
     */
    'reason': string;
}
export interface RepublishInvoicePdfRequest {
    /**
     * The stored version to publish, named by its document id — one of the entries `listInvoicePdfVersions` returns for this invoice. That list is what a practice picks from, so the request carries an id it was given rather than a version number it would have to count out.
     */
    'documentId': string;
}
export interface UpdateInvoicePaymentLinkRequest {
    /**
     * An absolute https URL, or null (or absent) to clear the link. Anything else is a 400.
     */
    'paymentLink'?: string | null;
}
export interface UpdateInvoiceStatusRequest {
    'status': InvoiceStatus;
}



/**
 * DebiteurenApi - axios parameter creator
 */
export const DebiteurenApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Create a debiteur
         * @param {DebiteurRequest} debiteurRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createDebiteur: async (debiteurRequest: DebiteurRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'debiteurRequest' is not null or undefined
            assertParamExists('createDebiteur', 'debiteurRequest', debiteurRequest)
            const localVarPath = `/api/v1/debiteuren`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(debiteurRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * A zorgtraject of any regime can carry a debiteur; a Jeugdwet zorgtraject with one is the onderaannemer case. PRIVATE billing lines of the zorgtraject dated inside the period are invoiced to the debiteur. A period overlapping another of the same zorgtraject is refused. 
         * @summary Name a debiteur on a zorgtraject for a period
         * @param {string} zorgtrajectId Zorgtraject (care trajectory) UUID
         * @param {DebiteurPeriodeRequest} debiteurPeriodeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createDebiteurPeriode: async (zorgtrajectId: string, debiteurPeriodeRequest: DebiteurPeriodeRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('createDebiteurPeriode', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'debiteurPeriodeRequest' is not null or undefined
            assertParamExists('createDebiteurPeriode', 'debiteurPeriodeRequest', debiteurPeriodeRequest)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/debiteur-perioden`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(debiteurPeriodeRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Only a debiteur nothing names can be deleted. One an invoice was addressed to, or one still named on a zorgtraject, is refused with 409 — deactivate it instead. 
         * @summary Delete a debiteur
         * @param {string} debiteurId Debiteur UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteDebiteur: async (debiteurId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'debiteurId' is not null or undefined
            assertParamExists('deleteDebiteur', 'debiteurId', debiteurId)
            const localVarPath = `/api/v1/debiteuren/{debiteurId}`
                .replace(`{${"debiteurId"}}`, encodeURIComponent(String(debiteurId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Remove a debiteur period
         * @param {string} zorgtrajectId Zorgtraject (care trajectory) UUID
         * @param {string} periodeId Debiteur period UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteDebiteurPeriode: async (zorgtrajectId: string, periodeId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('deleteDebiteurPeriode', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'periodeId' is not null or undefined
            assertParamExists('deleteDebiteurPeriode', 'periodeId', periodeId)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/debiteur-perioden/{periodeId}`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)))
                .replace(`{${"periodeId"}}`, encodeURIComponent(String(periodeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get a debiteur
         * @param {string} debiteurId Debiteur UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getDebiteur: async (debiteurId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'debiteurId' is not null or undefined
            assertParamExists('getDebiteur', 'debiteurId', debiteurId)
            const localVarPath = `/api/v1/debiteuren/{debiteurId}`
                .replace(`{${"debiteurId"}}`, encodeURIComponent(String(debiteurId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * \"Factuur aan\": the periods in which this zorgtraject\'s particulier billing lines are invoiced to a debiteur rather than to the cliënt (#4750). Oldest first; periods never overlap. 
         * @summary List a zorgtraject\'s debiteur periods
         * @param {string} zorgtrajectId Zorgtraject (care trajectory) UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listDebiteurPerioden: async (zorgtrajectId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('listDebiteurPerioden', 'zorgtrajectId', zorgtrajectId)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/debiteur-perioden`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The organisation\'s register of third parties a particulier invoice can be addressed to instead of the cliënt — a hoofdaannemer paying its onderaannemer, an employer (#4750, ADR-030 A10). Ordered by naam. Deactivated debiteuren are left out unless asked for. 
         * @summary List debiteuren
         * @param {boolean} [includeInactive] Include deactivated debiteuren
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listDebiteuren: async (includeInactive?: boolean, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/debiteuren`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (includeInactive !== undefined) {
                localVarQueryParameter['includeInactive'] = includeInactive;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Rewrites every field. Deactivating (actief false) takes a debiteur out of the pickers; a zorgtraject period that names it still applies. An issued invoice keeps the document it was sent with; a draft renders the debiteur as it now stands. 
         * @summary Update a debiteur
         * @param {string} debiteurId Debiteur UUID
         * @param {DebiteurRequest} debiteurRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateDebiteur: async (debiteurId: string, debiteurRequest: DebiteurRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'debiteurId' is not null or undefined
            assertParamExists('updateDebiteur', 'debiteurId', debiteurId)
            // verify required parameter 'debiteurRequest' is not null or undefined
            assertParamExists('updateDebiteur', 'debiteurRequest', debiteurRequest)
            const localVarPath = `/api/v1/debiteuren/{debiteurId}`
                .replace(`{${"debiteurId"}}`, encodeURIComponent(String(debiteurId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(debiteurRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Invoices already made under the period keep the debiteur they were addressed to. 
         * @summary Change a debiteur period
         * @param {string} zorgtrajectId Zorgtraject (care trajectory) UUID
         * @param {string} periodeId Debiteur period UUID
         * @param {DebiteurPeriodeRequest} debiteurPeriodeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateDebiteurPeriode: async (zorgtrajectId: string, periodeId: string, debiteurPeriodeRequest: DebiteurPeriodeRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'zorgtrajectId' is not null or undefined
            assertParamExists('updateDebiteurPeriode', 'zorgtrajectId', zorgtrajectId)
            // verify required parameter 'periodeId' is not null or undefined
            assertParamExists('updateDebiteurPeriode', 'periodeId', periodeId)
            // verify required parameter 'debiteurPeriodeRequest' is not null or undefined
            assertParamExists('updateDebiteurPeriode', 'debiteurPeriodeRequest', debiteurPeriodeRequest)
            const localVarPath = `/api/v1/zorgtrajecten/{zorgtrajectId}/debiteur-perioden/{periodeId}`
                .replace(`{${"zorgtrajectId"}}`, encodeURIComponent(String(zorgtrajectId)))
                .replace(`{${"periodeId"}}`, encodeURIComponent(String(periodeId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(debiteurPeriodeRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * DebiteurenApi - functional programming interface
 */
export const DebiteurenApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = DebiteurenApiAxiosParamCreator(configuration)
    return {
        /**
         * 
         * @summary Create a debiteur
         * @param {DebiteurRequest} debiteurRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createDebiteur(debiteurRequest: DebiteurRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DebiteurResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createDebiteur(debiteurRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DebiteurenApi.createDebiteur']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * A zorgtraject of any regime can carry a debiteur; a Jeugdwet zorgtraject with one is the onderaannemer case. PRIVATE billing lines of the zorgtraject dated inside the period are invoiced to the debiteur. A period overlapping another of the same zorgtraject is refused. 
         * @summary Name a debiteur on a zorgtraject for a period
         * @param {string} zorgtrajectId Zorgtraject (care trajectory) UUID
         * @param {DebiteurPeriodeRequest} debiteurPeriodeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createDebiteurPeriode(zorgtrajectId: string, debiteurPeriodeRequest: DebiteurPeriodeRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DebiteurPeriodeResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createDebiteurPeriode(zorgtrajectId, debiteurPeriodeRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DebiteurenApi.createDebiteurPeriode']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Only a debiteur nothing names can be deleted. One an invoice was addressed to, or one still named on a zorgtraject, is refused with 409 — deactivate it instead. 
         * @summary Delete a debiteur
         * @param {string} debiteurId Debiteur UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteDebiteur(debiteurId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteDebiteur(debiteurId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DebiteurenApi.deleteDebiteur']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Remove a debiteur period
         * @param {string} zorgtrajectId Zorgtraject (care trajectory) UUID
         * @param {string} periodeId Debiteur period UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteDebiteurPeriode(zorgtrajectId: string, periodeId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteDebiteurPeriode(zorgtrajectId, periodeId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DebiteurenApi.deleteDebiteurPeriode']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * 
         * @summary Get a debiteur
         * @param {string} debiteurId Debiteur UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getDebiteur(debiteurId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DebiteurResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getDebiteur(debiteurId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DebiteurenApi.getDebiteur']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * \"Factuur aan\": the periods in which this zorgtraject\'s particulier billing lines are invoiced to a debiteur rather than to the cliënt (#4750). Oldest first; periods never overlap. 
         * @summary List a zorgtraject\'s debiteur periods
         * @param {string} zorgtrajectId Zorgtraject (care trajectory) UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listDebiteurPerioden(zorgtrajectId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<DebiteurPeriodeResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listDebiteurPerioden(zorgtrajectId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DebiteurenApi.listDebiteurPerioden']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The organisation\'s register of third parties a particulier invoice can be addressed to instead of the cliënt — a hoofdaannemer paying its onderaannemer, an employer (#4750, ADR-030 A10). Ordered by naam. Deactivated debiteuren are left out unless asked for. 
         * @summary List debiteuren
         * @param {boolean} [includeInactive] Include deactivated debiteuren
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listDebiteuren(includeInactive?: boolean, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<DebiteurResponse>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listDebiteuren(includeInactive, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DebiteurenApi.listDebiteuren']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Rewrites every field. Deactivating (actief false) takes a debiteur out of the pickers; a zorgtraject period that names it still applies. An issued invoice keeps the document it was sent with; a draft renders the debiteur as it now stands. 
         * @summary Update a debiteur
         * @param {string} debiteurId Debiteur UUID
         * @param {DebiteurRequest} debiteurRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateDebiteur(debiteurId: string, debiteurRequest: DebiteurRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DebiteurResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateDebiteur(debiteurId, debiteurRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DebiteurenApi.updateDebiteur']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Invoices already made under the period keep the debiteur they were addressed to. 
         * @summary Change a debiteur period
         * @param {string} zorgtrajectId Zorgtraject (care trajectory) UUID
         * @param {string} periodeId Debiteur period UUID
         * @param {DebiteurPeriodeRequest} debiteurPeriodeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateDebiteurPeriode(zorgtrajectId: string, periodeId: string, debiteurPeriodeRequest: DebiteurPeriodeRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<DebiteurPeriodeResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateDebiteurPeriode(zorgtrajectId, periodeId, debiteurPeriodeRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['DebiteurenApi.updateDebiteurPeriode']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * DebiteurenApi - factory interface
 */
export const DebiteurenApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = DebiteurenApiFp(configuration)
    return {
        /**
         * 
         * @summary Create a debiteur
         * @param {DebiteurRequest} debiteurRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createDebiteur(debiteurRequest: DebiteurRequest, options?: RawAxiosRequestConfig): AxiosPromise<DebiteurResponse> {
            return localVarFp.createDebiteur(debiteurRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * A zorgtraject of any regime can carry a debiteur; a Jeugdwet zorgtraject with one is the onderaannemer case. PRIVATE billing lines of the zorgtraject dated inside the period are invoiced to the debiteur. A period overlapping another of the same zorgtraject is refused. 
         * @summary Name a debiteur on a zorgtraject for a period
         * @param {string} zorgtrajectId Zorgtraject (care trajectory) UUID
         * @param {DebiteurPeriodeRequest} debiteurPeriodeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createDebiteurPeriode(zorgtrajectId: string, debiteurPeriodeRequest: DebiteurPeriodeRequest, options?: RawAxiosRequestConfig): AxiosPromise<DebiteurPeriodeResponse> {
            return localVarFp.createDebiteurPeriode(zorgtrajectId, debiteurPeriodeRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Only a debiteur nothing names can be deleted. One an invoice was addressed to, or one still named on a zorgtraject, is refused with 409 — deactivate it instead. 
         * @summary Delete a debiteur
         * @param {string} debiteurId Debiteur UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteDebiteur(debiteurId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteDebiteur(debiteurId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Remove a debiteur period
         * @param {string} zorgtrajectId Zorgtraject (care trajectory) UUID
         * @param {string} periodeId Debiteur period UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteDebiteurPeriode(zorgtrajectId: string, periodeId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteDebiteurPeriode(zorgtrajectId, periodeId, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get a debiteur
         * @param {string} debiteurId Debiteur UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getDebiteur(debiteurId: string, options?: RawAxiosRequestConfig): AxiosPromise<DebiteurResponse> {
            return localVarFp.getDebiteur(debiteurId, options).then((request) => request(axios, basePath));
        },
        /**
         * \"Factuur aan\": the periods in which this zorgtraject\'s particulier billing lines are invoiced to a debiteur rather than to the cliënt (#4750). Oldest first; periods never overlap. 
         * @summary List a zorgtraject\'s debiteur periods
         * @param {string} zorgtrajectId Zorgtraject (care trajectory) UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listDebiteurPerioden(zorgtrajectId: string, options?: RawAxiosRequestConfig): AxiosPromise<Array<DebiteurPeriodeResponse>> {
            return localVarFp.listDebiteurPerioden(zorgtrajectId, options).then((request) => request(axios, basePath));
        },
        /**
         * The organisation\'s register of third parties a particulier invoice can be addressed to instead of the cliënt — a hoofdaannemer paying its onderaannemer, an employer (#4750, ADR-030 A10). Ordered by naam. Deactivated debiteuren are left out unless asked for. 
         * @summary List debiteuren
         * @param {boolean} [includeInactive] Include deactivated debiteuren
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listDebiteuren(includeInactive?: boolean, options?: RawAxiosRequestConfig): AxiosPromise<Array<DebiteurResponse>> {
            return localVarFp.listDebiteuren(includeInactive, options).then((request) => request(axios, basePath));
        },
        /**
         * Rewrites every field. Deactivating (actief false) takes a debiteur out of the pickers; a zorgtraject period that names it still applies. An issued invoice keeps the document it was sent with; a draft renders the debiteur as it now stands. 
         * @summary Update a debiteur
         * @param {string} debiteurId Debiteur UUID
         * @param {DebiteurRequest} debiteurRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateDebiteur(debiteurId: string, debiteurRequest: DebiteurRequest, options?: RawAxiosRequestConfig): AxiosPromise<DebiteurResponse> {
            return localVarFp.updateDebiteur(debiteurId, debiteurRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Invoices already made under the period keep the debiteur they were addressed to. 
         * @summary Change a debiteur period
         * @param {string} zorgtrajectId Zorgtraject (care trajectory) UUID
         * @param {string} periodeId Debiteur period UUID
         * @param {DebiteurPeriodeRequest} debiteurPeriodeRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateDebiteurPeriode(zorgtrajectId: string, periodeId: string, debiteurPeriodeRequest: DebiteurPeriodeRequest, options?: RawAxiosRequestConfig): AxiosPromise<DebiteurPeriodeResponse> {
            return localVarFp.updateDebiteurPeriode(zorgtrajectId, periodeId, debiteurPeriodeRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * DebiteurenApi - object-oriented interface
 */
export class DebiteurenApi extends BaseAPI {
    /**
     * 
     * @summary Create a debiteur
     * @param {DebiteurRequest} debiteurRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createDebiteur(debiteurRequest: DebiteurRequest, options?: RawAxiosRequestConfig) {
        return DebiteurenApiFp(this.configuration).createDebiteur(debiteurRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * A zorgtraject of any regime can carry a debiteur; a Jeugdwet zorgtraject with one is the onderaannemer case. PRIVATE billing lines of the zorgtraject dated inside the period are invoiced to the debiteur. A period overlapping another of the same zorgtraject is refused. 
     * @summary Name a debiteur on a zorgtraject for a period
     * @param {string} zorgtrajectId Zorgtraject (care trajectory) UUID
     * @param {DebiteurPeriodeRequest} debiteurPeriodeRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createDebiteurPeriode(zorgtrajectId: string, debiteurPeriodeRequest: DebiteurPeriodeRequest, options?: RawAxiosRequestConfig) {
        return DebiteurenApiFp(this.configuration).createDebiteurPeriode(zorgtrajectId, debiteurPeriodeRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Only a debiteur nothing names can be deleted. One an invoice was addressed to, or one still named on a zorgtraject, is refused with 409 — deactivate it instead. 
     * @summary Delete a debiteur
     * @param {string} debiteurId Debiteur UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteDebiteur(debiteurId: string, options?: RawAxiosRequestConfig) {
        return DebiteurenApiFp(this.configuration).deleteDebiteur(debiteurId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Remove a debiteur period
     * @param {string} zorgtrajectId Zorgtraject (care trajectory) UUID
     * @param {string} periodeId Debiteur period UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteDebiteurPeriode(zorgtrajectId: string, periodeId: string, options?: RawAxiosRequestConfig) {
        return DebiteurenApiFp(this.configuration).deleteDebiteurPeriode(zorgtrajectId, periodeId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * 
     * @summary Get a debiteur
     * @param {string} debiteurId Debiteur UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getDebiteur(debiteurId: string, options?: RawAxiosRequestConfig) {
        return DebiteurenApiFp(this.configuration).getDebiteur(debiteurId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * \"Factuur aan\": the periods in which this zorgtraject\'s particulier billing lines are invoiced to a debiteur rather than to the cliënt (#4750). Oldest first; periods never overlap. 
     * @summary List a zorgtraject\'s debiteur periods
     * @param {string} zorgtrajectId Zorgtraject (care trajectory) UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listDebiteurPerioden(zorgtrajectId: string, options?: RawAxiosRequestConfig) {
        return DebiteurenApiFp(this.configuration).listDebiteurPerioden(zorgtrajectId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The organisation\'s register of third parties a particulier invoice can be addressed to instead of the cliënt — a hoofdaannemer paying its onderaannemer, an employer (#4750, ADR-030 A10). Ordered by naam. Deactivated debiteuren are left out unless asked for. 
     * @summary List debiteuren
     * @param {boolean} [includeInactive] Include deactivated debiteuren
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listDebiteuren(includeInactive?: boolean, options?: RawAxiosRequestConfig) {
        return DebiteurenApiFp(this.configuration).listDebiteuren(includeInactive, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Rewrites every field. Deactivating (actief false) takes a debiteur out of the pickers; a zorgtraject period that names it still applies. An issued invoice keeps the document it was sent with; a draft renders the debiteur as it now stands. 
     * @summary Update a debiteur
     * @param {string} debiteurId Debiteur UUID
     * @param {DebiteurRequest} debiteurRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateDebiteur(debiteurId: string, debiteurRequest: DebiteurRequest, options?: RawAxiosRequestConfig) {
        return DebiteurenApiFp(this.configuration).updateDebiteur(debiteurId, debiteurRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Invoices already made under the period keep the debiteur they were addressed to. 
     * @summary Change a debiteur period
     * @param {string} zorgtrajectId Zorgtraject (care trajectory) UUID
     * @param {string} periodeId Debiteur period UUID
     * @param {DebiteurPeriodeRequest} debiteurPeriodeRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateDebiteurPeriode(zorgtrajectId: string, periodeId: string, debiteurPeriodeRequest: DebiteurPeriodeRequest, options?: RawAxiosRequestConfig) {
        return DebiteurenApiFp(this.configuration).updateDebiteurPeriode(zorgtrajectId, periodeId, debiteurPeriodeRequest, options).then((request) => request(this.axios, this.basePath));
    }
}



/**
 * InvoicesApi - axios parameter creator
 */
export const InvoicesApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * Create a new invoice from billing lines. All billing lines must belong to the same client. 
         * @summary Create invoice
         * @param {CreateInvoiceRequest} createInvoiceRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createInvoice: async (createInvoiceRequest: CreateInvoiceRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'createInvoiceRequest' is not null or undefined
            assertParamExists('createInvoice', 'createInvoiceRequest', createInvoiceRequest)
            const localVarPath = `/api/v1/invoices`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createInvoiceRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Creates invoices from a set of billing lines, grouping them by client. One invoice is created per client. This is the INVOICE billing method flow where billing lines go directly to invoices (skipping batches).  Validates that all required ZPM fields are present before creating invoices. Returns validation errors if any billing line is missing required data. 
         * @summary Create invoices from billing lines
         * @param {CreateInvoicesFromBillingLinesRequest} createInvoicesFromBillingLinesRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createInvoicesFromBillingLines: async (createInvoicesFromBillingLinesRequest: CreateInvoicesFromBillingLinesRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'createInvoicesFromBillingLinesRequest' is not null or undefined
            assertParamExists('createInvoicesFromBillingLines', 'createInvoicesFromBillingLinesRequest', createInvoicesFromBillingLinesRequest)
            const localVarPath = `/api/v1/invoices/from-billing-lines`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(createInvoicesFromBillingLinesRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Reverse an issued invoice. The original invoice is preserved and moved to CANCELLED (keeping the gapless sequence intact), and a new credit invoice with negative totals is created as a DRAFT, linked back to the original via originalInvoiceId. The credit invoice draws its own gapless number only when it is itself issued (DRAFT -> SENT).  Full credit only (MVP): the whole invoice is reversed. A DRAFT invoice cannot be credited — it never consumed a number and is deleted instead. PAID and CANCELLED invoices are terminal and cannot be credited. 
         * @summary Credit (void) an issued invoice
         * @param {string} invoiceId Invoice UUID to credit
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        creditInvoice: async (invoiceId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invoiceId' is not null or undefined
            assertParamExists('creditInvoice', 'invoiceId', invoiceId)
            const localVarPath = `/api/v1/invoices/{invoiceId}/credit`
                .replace(`{${"invoiceId"}}`, encodeURIComponent(String(invoiceId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Deletes a draft invoice. Only invoices in DRAFT status can be deleted. 
         * @summary Delete invoice
         * @param {string} invoiceId Invoice UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteInvoice: async (invoiceId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invoiceId' is not null or undefined
            assertParamExists('deleteInvoice', 'invoiceId', invoiceId)
            const localVarPath = `/api/v1/invoices/{invoiceId}`
                .replace(`{${"invoiceId"}}`, encodeURIComponent(String(invoiceId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Generate a combined PDF containing multiple invoices.  Each invoice contributes its stored document when it has one. An issued invoice with no stored document is rendered now and that render is filed as its document, marked `RECONSTRUCTED` — so the export converges the invoices issued before their PDF was stored at all, without claiming the render is the document the client received. A second export of the same invoice reuses it. A draft carries no invoice number and is never filed.
         * @summary Download batch PDF
         * @param {BatchPdfRequest} batchPdfRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadBatchPdf: async (batchPdfRequest: BatchPdfRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'batchPdfRequest' is not null or undefined
            assertParamExists('downloadBatchPdf', 'batchPdfRequest', batchPdfRequest)
            const localVarPath = `/api/v1/invoices/batch-pdf`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/pdf,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(batchPdfRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Download the invoice as a PDF document.  When the invoice has a stored document (see `document` on the invoice response), those bytes are served unchanged — the document the client received is reproducible, and does not silently change when the client\'s address, the regiebehandelaar, the diagnosis or the template does.  An invoice with no stored document is rendered on the fly from today\'s data. A download never stores what it renders: a read must not create an archival record.
         * @summary Download invoice PDF
         * @param {string} invoiceId Invoice UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadInvoicePdf: async (invoiceId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invoiceId' is not null or undefined
            assertParamExists('downloadInvoicePdf', 'invoiceId', invoiceId)
            const localVarPath = `/api/v1/invoices/{invoiceId}/pdf`
                .replace(`{${"invoiceId"}}`, encodeURIComponent(String(invoiceId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/pdf,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Exports multiple invoices as either a combined PDF or a ZIP archive containing individual PDFs. - format=pdf: Returns a single PDF with all invoices combined - format=zip: Returns a ZIP archive with individual PDF files for each invoice  Both formats serve an invoice\'s stored document when it has one, and file a `RECONSTRUCTED` document for an issued invoice that has none — see `downloadBatchPdf`. 
         * @summary Export multiple invoices
         * @param {string} invoiceIds Comma-separated list of invoice UUIDs
         * @param {ExportInvoicesFormatEnum} format Export format (pdf for combined PDF, zip for individual PDFs in ZIP archive)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        exportInvoices: async (invoiceIds: string, format: ExportInvoicesFormatEnum, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invoiceIds' is not null or undefined
            assertParamExists('exportInvoices', 'invoiceIds', invoiceIds)
            // verify required parameter 'format' is not null or undefined
            assertParamExists('exportInvoices', 'format', format)
            const localVarPath = `/api/v1/invoices/export`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (invoiceIds !== undefined) {
                localVarQueryParameter['invoiceIds'] = invoiceIds;
            }

            if (format !== undefined) {
                localVarQueryParameter['format'] = format;
            }

            localVarHeaderParameter['Accept'] = 'application/pdf,application/zip,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * The invoice whose published PDF is the given document, so a screen showing the document — the \"zichtbaar maken\" dialog in the cliëntdossier — can offer what belongs to the invoice, such as its payment link. 404 when the document is no invoice\'s published PDF.
         * @summary Get the invoice a dossier document publishes
         * @param {string} documentId Document UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getInvoiceByDocument: async (documentId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'documentId' is not null or undefined
            assertParamExists('getInvoiceByDocument', 'documentId', documentId)
            const localVarPath = `/api/v1/invoices/by-document/{documentId}`
                .replace(`{${"documentId"}}`, encodeURIComponent(String(documentId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Retrieves a single invoice by ID with its line items.
         * @summary Get invoice by ID
         * @param {string} invoiceId Invoice UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getInvoiceById: async (invoiceId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invoiceId' is not null or undefined
            assertParamExists('getInvoiceById', 'invoiceId', invoiceId)
            const localVarPath = `/api/v1/invoices/{invoiceId}`
                .replace(`{${"invoiceId"}}`, encodeURIComponent(String(invoiceId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Every stored version of this invoice\'s PDF, oldest first. Empty when nothing is stored — an unissued draft, or an invoice issued before its PDF was stored at all.  On the normal path this holds exactly one entry; a second exists only where someone deliberately regenerated. Deliberately unpaged: a version chain is bounded by how often a practice regenerates one nota, not by the size of the organisation.
         * @summary List the stored versions of an invoice PDF
         * @param {string} invoiceId Invoice UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listInvoicePdfVersions: async (invoiceId: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invoiceId' is not null or undefined
            assertParamExists('listInvoicePdfVersions', 'invoiceId', invoiceId)
            const localVarPath = `/api/v1/invoices/{invoiceId}/pdf/versions`
                .replace(`{${"invoiceId"}}`, encodeURIComponent(String(invoiceId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * List invoices with pagination and filtering.
         * @summary List invoices
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;invoiceDate,desc\&quot;). Supported fields: invoiceDate, dueDate, invoiceNumber, status, totalEuro, paidEuro, createdAt, updatedAt. Any other field falls back to invoiceDate.
         * @param {string} [clientId] Filter by client
         * @param {InvoiceStatus} [status] Filter by status. SENT and OVERDUE are *derived* buckets rather than stored values and are disjoint: OVERDUE matches an issued invoice whose due date has passed (or the rarely stored OVERDUE status), SENT matches an issued invoice that is not yet due. DRAFT, PAID and CANCELLED match the stored status.
         * @param {string} [startDate] Filter by invoice date start
         * @param {string} [endDate] Filter by invoice date end
         * @param {string} [search] Filter by invoice number or client name (partial match, case-insensitive). The client name is matched as it is rendered in the list — first name, tussenvoegsel and last name.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listInvoices: async (page?: number, size?: number, sort?: string, clientId?: string, status?: InvoiceStatus, startDate?: string, endDate?: string, search?: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/api/v1/invoices`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            if (page !== undefined) {
                localVarQueryParameter['page'] = page;
            }

            if (size !== undefined) {
                localVarQueryParameter['size'] = size;
            }

            if (sort !== undefined) {
                localVarQueryParameter['sort'] = sort;
            }

            if (clientId !== undefined) {
                localVarQueryParameter['clientId'] = clientId;
            }

            if (status !== undefined) {
                localVarQueryParameter['status'] = status;
            }

            if (startDate !== undefined) {
                localVarQueryParameter['startDate'] = (startDate as any instanceof Date) ?
                    (startDate as any).toISOString().substring(0,10) :
                    startDate;
            }

            if (endDate !== undefined) {
                localVarQueryParameter['endDate'] = (endDate as any instanceof Date) ?
                    (endDate as any).toISOString().substring(0,10) :
                    endDate;
            }

            if (search !== undefined) {
                localVarQueryParameter['search'] = search;
            }

            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Record a payment on an invoice. Only allowed for SENT or OVERDUE invoices.
         * @summary Record payment
         * @param {string} invoiceId Invoice UUID
         * @param {RecordPaymentRequest} recordPaymentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        recordPayment: async (invoiceId: string, recordPaymentRequest: RecordPaymentRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invoiceId' is not null or undefined
            assertParamExists('recordPayment', 'invoiceId', invoiceId)
            // verify required parameter 'recordPaymentRequest' is not null or undefined
            assertParamExists('recordPayment', 'recordPaymentRequest', recordPaymentRequest)
            const localVarPath = `/api/v1/invoices/{invoiceId}/payments`
                .replace(`{${"invoiceId"}}`, encodeURIComponent(String(invoiceId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(recordPaymentRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Render the invoice PDF again from today\'s data and **append** it to the stored document\'s version chain. Requires `billing.update`; the download is `billing.read`, because rendering a new version is a change to the dossier rather than a read of it.  The previous version is never touched. Overwriting the bytes a client received would re-create the very problem storing them solves, so a regeneration adds a version and leaves version 1 — the document the client received — exactly where it is (#3515 Q3). `document` on the invoice response keeps pointing at the version the client already has, which is also what the client portal keeps serving: making a newer version client-facing is a separate, deliberate act, and it is `republishInvoicePdf` (#3986).  Only an issued invoice (SENT/PAID/OVERDUE) can be regenerated, and only one that already has a stored document — there is nothing to append a version to otherwise. A draft is still editable and its preview is rendered on the fly, never stored.  The reason is required and is kept on the new version, so the version list can say why it exists.
         * @summary Regenerate the invoice PDF as a new version
         * @param {string} invoiceId Invoice UUID
         * @param {RegenerateInvoicePdfRequest} regenerateInvoicePdfRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        regenerateInvoicePdf: async (invoiceId: string, regenerateInvoicePdfRequest: RegenerateInvoicePdfRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invoiceId' is not null or undefined
            assertParamExists('regenerateInvoicePdf', 'invoiceId', invoiceId)
            // verify required parameter 'regenerateInvoicePdfRequest' is not null or undefined
            assertParamExists('regenerateInvoicePdf', 'regenerateInvoicePdfRequest', regenerateInvoicePdfRequest)
            const localVarPath = `/api/v1/invoices/{invoiceId}/pdf/regenerate`
                .replace(`{${"invoiceId"}}`, encodeURIComponent(String(invoiceId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(regenerateInvoicePdfRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Make an already stored version of this invoice\'s PDF the one the client sees. Requires `billing.update`.  Regenerating deliberately leaves the client on the version they received (#3967); this is the separate, deliberate act that moves them. Were repointing a side effect of regenerating, a practice re-rendering to check a layout change would silently republish.  The whole of it is moving the invoice\'s `document` reference: the portal resolves the PDF from it and takes no version, so the document it names *is* the version a client sees. Every earlier version keeps its row, its bytes and its reason, stays in `listInvoicePdfVersions`, and stops being client-facing by the same fact that makes the new one client-facing.  `documentId` must name a version of **this invoice\'s own** PDF chain — one of the entries `listInvoicePdfVersions` returns — and anything else is refused. A document id is not authority on its own, and repointing at another invoice\'s nota is the one way this could show a client the wrong bytes.  Portal exposure is not changed here: it is a property of the document and a behandelaar\'s to grant (#3968), so a version that has not been exposed stays unreadable to the client exactly as version 1 would be.
         * @summary Publish a stored version of the invoice PDF to the client
         * @param {string} invoiceId Invoice UUID
         * @param {RepublishInvoicePdfRequest} republishInvoicePdfRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        republishInvoicePdf: async (invoiceId: string, republishInvoicePdfRequest: RepublishInvoicePdfRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invoiceId' is not null or undefined
            assertParamExists('republishInvoicePdf', 'invoiceId', invoiceId)
            // verify required parameter 'republishInvoicePdfRequest' is not null or undefined
            assertParamExists('republishInvoicePdf', 'republishInvoicePdfRequest', republishInvoicePdfRequest)
            const localVarPath = `/api/v1/invoices/{invoiceId}/pdf/publish`
                .replace(`{${"invoiceId"}}`, encodeURIComponent(String(invoiceId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(republishInvoicePdfRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Set the practice\'s own betaallink (Tikkie, Mollie, a bank request) on an issued invoice, or clear it with null. The cliënt sees it beside the invoice in the portaal and as a Betalen button in the \"staat voor je klaar\" mail. Only an absolute https URL is accepted, and a link can only be set while the invoice awaits payment (SENT or OVERDUE); clearing is always allowed. The change is audit-logged on the invoice.
         * @summary Set or clear the invoice\'s payment link
         * @param {string} invoiceId Invoice UUID
         * @param {UpdateInvoicePaymentLinkRequest} updateInvoicePaymentLinkRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateInvoicePaymentLink: async (invoiceId: string, updateInvoicePaymentLinkRequest: UpdateInvoicePaymentLinkRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invoiceId' is not null or undefined
            assertParamExists('updateInvoicePaymentLink', 'invoiceId', invoiceId)
            // verify required parameter 'updateInvoicePaymentLinkRequest' is not null or undefined
            assertParamExists('updateInvoicePaymentLink', 'updateInvoicePaymentLinkRequest', updateInvoicePaymentLinkRequest)
            const localVarPath = `/api/v1/invoices/{invoiceId}/payment-link`
                .replace(`{${"invoiceId"}}`, encodeURIComponent(String(invoiceId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateInvoicePaymentLinkRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
        /**
         * Update the status of an invoice. Valid transitions: - DRAFT -> SENT, CANCELLED - SENT -> PAID, OVERDUE, CANCELLED - OVERDUE -> PAID, CANCELLED 
         * @summary Update invoice status
         * @param {string} invoiceId Invoice UUID
         * @param {UpdateInvoiceStatusRequest} updateInvoiceStatusRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateInvoiceStatus: async (invoiceId: string, updateInvoiceStatusRequest: UpdateInvoiceStatusRequest, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invoiceId' is not null or undefined
            assertParamExists('updateInvoiceStatus', 'invoiceId', invoiceId)
            // verify required parameter 'updateInvoiceStatusRequest' is not null or undefined
            assertParamExists('updateInvoiceStatus', 'updateInvoiceStatusRequest', updateInvoiceStatusRequest)
            const localVarPath = `/api/v1/invoices/{invoiceId}/status`
                .replace(`{${"invoiceId"}}`, encodeURIComponent(String(invoiceId)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }

            const localVarRequestOptions = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication firebaseAuth required
            // http bearer authentication required
            await setBearerAuthToObject(localVarHeaderParameter, configuration)

            localVarHeaderParameter['Content-Type'] = 'application/json';
            localVarHeaderParameter['Accept'] = 'application/json,application/problem+json';

            setSearchParams(localVarUrlObj, localVarQueryParameter);
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            localVarRequestOptions.data = serializeDataIfNeeded(updateInvoiceStatusRequest, localVarRequestOptions, configuration)

            return {
                url: toPathString(localVarUrlObj),
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * InvoicesApi - functional programming interface
 */
export const InvoicesApiFp = function(configuration?: Configuration) {
    const localVarAxiosParamCreator = InvoicesApiAxiosParamCreator(configuration)
    return {
        /**
         * Create a new invoice from billing lines. All billing lines must belong to the same client. 
         * @summary Create invoice
         * @param {CreateInvoiceRequest} createInvoiceRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createInvoice(createInvoiceRequest: CreateInvoiceRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<InvoiceResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createInvoice(createInvoiceRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InvoicesApi.createInvoice']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Creates invoices from a set of billing lines, grouping them by client. One invoice is created per client. This is the INVOICE billing method flow where billing lines go directly to invoices (skipping batches).  Validates that all required ZPM fields are present before creating invoices. Returns validation errors if any billing line is missing required data. 
         * @summary Create invoices from billing lines
         * @param {CreateInvoicesFromBillingLinesRequest} createInvoicesFromBillingLinesRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async createInvoicesFromBillingLines(createInvoicesFromBillingLinesRequest: CreateInvoicesFromBillingLinesRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<CreateInvoicesFromBillingLinesResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.createInvoicesFromBillingLines(createInvoicesFromBillingLinesRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InvoicesApi.createInvoicesFromBillingLines']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Reverse an issued invoice. The original invoice is preserved and moved to CANCELLED (keeping the gapless sequence intact), and a new credit invoice with negative totals is created as a DRAFT, linked back to the original via originalInvoiceId. The credit invoice draws its own gapless number only when it is itself issued (DRAFT -> SENT).  Full credit only (MVP): the whole invoice is reversed. A DRAFT invoice cannot be credited — it never consumed a number and is deleted instead. PAID and CANCELLED invoices are terminal and cannot be credited. 
         * @summary Credit (void) an issued invoice
         * @param {string} invoiceId Invoice UUID to credit
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async creditInvoice(invoiceId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<InvoiceResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.creditInvoice(invoiceId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InvoicesApi.creditInvoice']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Deletes a draft invoice. Only invoices in DRAFT status can be deleted. 
         * @summary Delete invoice
         * @param {string} invoiceId Invoice UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async deleteInvoice(invoiceId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<void>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.deleteInvoice(invoiceId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InvoicesApi.deleteInvoice']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Generate a combined PDF containing multiple invoices.  Each invoice contributes its stored document when it has one. An issued invoice with no stored document is rendered now and that render is filed as its document, marked `RECONSTRUCTED` — so the export converges the invoices issued before their PDF was stored at all, without claiming the render is the document the client received. A second export of the same invoice reuses it. A draft carries no invoice number and is never filed.
         * @summary Download batch PDF
         * @param {BatchPdfRequest} batchPdfRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async downloadBatchPdf(batchPdfRequest: BatchPdfRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.downloadBatchPdf(batchPdfRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InvoicesApi.downloadBatchPdf']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Download the invoice as a PDF document.  When the invoice has a stored document (see `document` on the invoice response), those bytes are served unchanged — the document the client received is reproducible, and does not silently change when the client\'s address, the regiebehandelaar, the diagnosis or the template does.  An invoice with no stored document is rendered on the fly from today\'s data. A download never stores what it renders: a read must not create an archival record.
         * @summary Download invoice PDF
         * @param {string} invoiceId Invoice UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async downloadInvoicePdf(invoiceId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.downloadInvoicePdf(invoiceId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InvoicesApi.downloadInvoicePdf']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Exports multiple invoices as either a combined PDF or a ZIP archive containing individual PDFs. - format=pdf: Returns a single PDF with all invoices combined - format=zip: Returns a ZIP archive with individual PDF files for each invoice  Both formats serve an invoice\'s stored document when it has one, and file a `RECONSTRUCTED` document for an issued invoice that has none — see `downloadBatchPdf`. 
         * @summary Export multiple invoices
         * @param {string} invoiceIds Comma-separated list of invoice UUIDs
         * @param {ExportInvoicesFormatEnum} format Export format (pdf for combined PDF, zip for individual PDFs in ZIP archive)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async exportInvoices(invoiceIds: string, format: ExportInvoicesFormatEnum, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<File>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.exportInvoices(invoiceIds, format, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InvoicesApi.exportInvoices']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * The invoice whose published PDF is the given document, so a screen showing the document — the \"zichtbaar maken\" dialog in the cliëntdossier — can offer what belongs to the invoice, such as its payment link. 404 when the document is no invoice\'s published PDF.
         * @summary Get the invoice a dossier document publishes
         * @param {string} documentId Document UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getInvoiceByDocument(documentId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<InvoiceResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getInvoiceByDocument(documentId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InvoicesApi.getInvoiceByDocument']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Retrieves a single invoice by ID with its line items.
         * @summary Get invoice by ID
         * @param {string} invoiceId Invoice UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async getInvoiceById(invoiceId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<InvoiceDetailResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.getInvoiceById(invoiceId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InvoicesApi.getInvoiceById']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Every stored version of this invoice\'s PDF, oldest first. Empty when nothing is stored — an unissued draft, or an invoice issued before its PDF was stored at all.  On the normal path this holds exactly one entry; a second exists only where someone deliberately regenerated. Deliberately unpaged: a version chain is bounded by how often a practice regenerates one nota, not by the size of the organisation.
         * @summary List the stored versions of an invoice PDF
         * @param {string} invoiceId Invoice UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listInvoicePdfVersions(invoiceId: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<InvoiceDocumentVersion>>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listInvoicePdfVersions(invoiceId, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InvoicesApi.listInvoicePdfVersions']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * List invoices with pagination and filtering.
         * @summary List invoices
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;invoiceDate,desc\&quot;). Supported fields: invoiceDate, dueDate, invoiceNumber, status, totalEuro, paidEuro, createdAt, updatedAt. Any other field falls back to invoiceDate.
         * @param {string} [clientId] Filter by client
         * @param {InvoiceStatus} [status] Filter by status. SENT and OVERDUE are *derived* buckets rather than stored values and are disjoint: OVERDUE matches an issued invoice whose due date has passed (or the rarely stored OVERDUE status), SENT matches an issued invoice that is not yet due. DRAFT, PAID and CANCELLED match the stored status.
         * @param {string} [startDate] Filter by invoice date start
         * @param {string} [endDate] Filter by invoice date end
         * @param {string} [search] Filter by invoice number or client name (partial match, case-insensitive). The client name is matched as it is rendered in the list — first name, tussenvoegsel and last name.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async listInvoices(page?: number, size?: number, sort?: string, clientId?: string, status?: InvoiceStatus, startDate?: string, endDate?: string, search?: string, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<InvoiceListResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.listInvoices(page, size, sort, clientId, status, startDate, endDate, search, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InvoicesApi.listInvoices']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Record a payment on an invoice. Only allowed for SENT or OVERDUE invoices.
         * @summary Record payment
         * @param {string} invoiceId Invoice UUID
         * @param {RecordPaymentRequest} recordPaymentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async recordPayment(invoiceId: string, recordPaymentRequest: RecordPaymentRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<InvoiceResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.recordPayment(invoiceId, recordPaymentRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InvoicesApi.recordPayment']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Render the invoice PDF again from today\'s data and **append** it to the stored document\'s version chain. Requires `billing.update`; the download is `billing.read`, because rendering a new version is a change to the dossier rather than a read of it.  The previous version is never touched. Overwriting the bytes a client received would re-create the very problem storing them solves, so a regeneration adds a version and leaves version 1 — the document the client received — exactly where it is (#3515 Q3). `document` on the invoice response keeps pointing at the version the client already has, which is also what the client portal keeps serving: making a newer version client-facing is a separate, deliberate act, and it is `republishInvoicePdf` (#3986).  Only an issued invoice (SENT/PAID/OVERDUE) can be regenerated, and only one that already has a stored document — there is nothing to append a version to otherwise. A draft is still editable and its preview is rendered on the fly, never stored.  The reason is required and is kept on the new version, so the version list can say why it exists.
         * @summary Regenerate the invoice PDF as a new version
         * @param {string} invoiceId Invoice UUID
         * @param {RegenerateInvoicePdfRequest} regenerateInvoicePdfRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async regenerateInvoicePdf(invoiceId: string, regenerateInvoicePdfRequest: RegenerateInvoicePdfRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<InvoiceDocumentVersion>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.regenerateInvoicePdf(invoiceId, regenerateInvoicePdfRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InvoicesApi.regenerateInvoicePdf']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Make an already stored version of this invoice\'s PDF the one the client sees. Requires `billing.update`.  Regenerating deliberately leaves the client on the version they received (#3967); this is the separate, deliberate act that moves them. Were repointing a side effect of regenerating, a practice re-rendering to check a layout change would silently republish.  The whole of it is moving the invoice\'s `document` reference: the portal resolves the PDF from it and takes no version, so the document it names *is* the version a client sees. Every earlier version keeps its row, its bytes and its reason, stays in `listInvoicePdfVersions`, and stops being client-facing by the same fact that makes the new one client-facing.  `documentId` must name a version of **this invoice\'s own** PDF chain — one of the entries `listInvoicePdfVersions` returns — and anything else is refused. A document id is not authority on its own, and repointing at another invoice\'s nota is the one way this could show a client the wrong bytes.  Portal exposure is not changed here: it is a property of the document and a behandelaar\'s to grant (#3968), so a version that has not been exposed stays unreadable to the client exactly as version 1 would be.
         * @summary Publish a stored version of the invoice PDF to the client
         * @param {string} invoiceId Invoice UUID
         * @param {RepublishInvoicePdfRequest} republishInvoicePdfRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async republishInvoicePdf(invoiceId: string, republishInvoicePdfRequest: RepublishInvoicePdfRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<InvoiceDocumentVersion>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.republishInvoicePdf(invoiceId, republishInvoicePdfRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InvoicesApi.republishInvoicePdf']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Set the practice\'s own betaallink (Tikkie, Mollie, a bank request) on an issued invoice, or clear it with null. The cliënt sees it beside the invoice in the portaal and as a Betalen button in the \"staat voor je klaar\" mail. Only an absolute https URL is accepted, and a link can only be set while the invoice awaits payment (SENT or OVERDUE); clearing is always allowed. The change is audit-logged on the invoice.
         * @summary Set or clear the invoice\'s payment link
         * @param {string} invoiceId Invoice UUID
         * @param {UpdateInvoicePaymentLinkRequest} updateInvoicePaymentLinkRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateInvoicePaymentLink(invoiceId: string, updateInvoicePaymentLinkRequest: UpdateInvoicePaymentLinkRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<InvoiceResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateInvoicePaymentLink(invoiceId, updateInvoicePaymentLinkRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InvoicesApi.updateInvoicePaymentLink']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
        /**
         * Update the status of an invoice. Valid transitions: - DRAFT -> SENT, CANCELLED - SENT -> PAID, OVERDUE, CANCELLED - OVERDUE -> PAID, CANCELLED 
         * @summary Update invoice status
         * @param {string} invoiceId Invoice UUID
         * @param {UpdateInvoiceStatusRequest} updateInvoiceStatusRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async updateInvoiceStatus(invoiceId: string, updateInvoiceStatusRequest: UpdateInvoiceStatusRequest, options?: RawAxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<InvoiceResponse>> {
            const localVarAxiosArgs = await localVarAxiosParamCreator.updateInvoiceStatus(invoiceId, updateInvoiceStatusRequest, options);
            const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
            const localVarOperationServerBasePath = operationServerMap['InvoicesApi.updateInvoiceStatus']?.[localVarOperationServerIndex]?.url;
            return (axios, basePath) => createRequestFunction(localVarAxiosArgs, globalAxios, BASE_PATH, configuration)(axios, localVarOperationServerBasePath || basePath);
        },
    }
};

/**
 * InvoicesApi - factory interface
 */
export const InvoicesApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    const localVarFp = InvoicesApiFp(configuration)
    return {
        /**
         * Create a new invoice from billing lines. All billing lines must belong to the same client. 
         * @summary Create invoice
         * @param {CreateInvoiceRequest} createInvoiceRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createInvoice(createInvoiceRequest: CreateInvoiceRequest, options?: RawAxiosRequestConfig): AxiosPromise<InvoiceResponse> {
            return localVarFp.createInvoice(createInvoiceRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Creates invoices from a set of billing lines, grouping them by client. One invoice is created per client. This is the INVOICE billing method flow where billing lines go directly to invoices (skipping batches).  Validates that all required ZPM fields are present before creating invoices. Returns validation errors if any billing line is missing required data. 
         * @summary Create invoices from billing lines
         * @param {CreateInvoicesFromBillingLinesRequest} createInvoicesFromBillingLinesRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        createInvoicesFromBillingLines(createInvoicesFromBillingLinesRequest: CreateInvoicesFromBillingLinesRequest, options?: RawAxiosRequestConfig): AxiosPromise<CreateInvoicesFromBillingLinesResponse> {
            return localVarFp.createInvoicesFromBillingLines(createInvoicesFromBillingLinesRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Reverse an issued invoice. The original invoice is preserved and moved to CANCELLED (keeping the gapless sequence intact), and a new credit invoice with negative totals is created as a DRAFT, linked back to the original via originalInvoiceId. The credit invoice draws its own gapless number only when it is itself issued (DRAFT -> SENT).  Full credit only (MVP): the whole invoice is reversed. A DRAFT invoice cannot be credited — it never consumed a number and is deleted instead. PAID and CANCELLED invoices are terminal and cannot be credited. 
         * @summary Credit (void) an issued invoice
         * @param {string} invoiceId Invoice UUID to credit
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        creditInvoice(invoiceId: string, options?: RawAxiosRequestConfig): AxiosPromise<InvoiceResponse> {
            return localVarFp.creditInvoice(invoiceId, options).then((request) => request(axios, basePath));
        },
        /**
         * Deletes a draft invoice. Only invoices in DRAFT status can be deleted. 
         * @summary Delete invoice
         * @param {string} invoiceId Invoice UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        deleteInvoice(invoiceId: string, options?: RawAxiosRequestConfig): AxiosPromise<void> {
            return localVarFp.deleteInvoice(invoiceId, options).then((request) => request(axios, basePath));
        },
        /**
         * Generate a combined PDF containing multiple invoices.  Each invoice contributes its stored document when it has one. An issued invoice with no stored document is rendered now and that render is filed as its document, marked `RECONSTRUCTED` — so the export converges the invoices issued before their PDF was stored at all, without claiming the render is the document the client received. A second export of the same invoice reuses it. A draft carries no invoice number and is never filed.
         * @summary Download batch PDF
         * @param {BatchPdfRequest} batchPdfRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadBatchPdf(batchPdfRequest: BatchPdfRequest, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.downloadBatchPdf(batchPdfRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Download the invoice as a PDF document.  When the invoice has a stored document (see `document` on the invoice response), those bytes are served unchanged — the document the client received is reproducible, and does not silently change when the client\'s address, the regiebehandelaar, the diagnosis or the template does.  An invoice with no stored document is rendered on the fly from today\'s data. A download never stores what it renders: a read must not create an archival record.
         * @summary Download invoice PDF
         * @param {string} invoiceId Invoice UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        downloadInvoicePdf(invoiceId: string, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.downloadInvoicePdf(invoiceId, options).then((request) => request(axios, basePath));
        },
        /**
         * Exports multiple invoices as either a combined PDF or a ZIP archive containing individual PDFs. - format=pdf: Returns a single PDF with all invoices combined - format=zip: Returns a ZIP archive with individual PDF files for each invoice  Both formats serve an invoice\'s stored document when it has one, and file a `RECONSTRUCTED` document for an issued invoice that has none — see `downloadBatchPdf`. 
         * @summary Export multiple invoices
         * @param {string} invoiceIds Comma-separated list of invoice UUIDs
         * @param {ExportInvoicesFormatEnum} format Export format (pdf for combined PDF, zip for individual PDFs in ZIP archive)
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        exportInvoices(invoiceIds: string, format: ExportInvoicesFormatEnum, options?: RawAxiosRequestConfig): AxiosPromise<File> {
            return localVarFp.exportInvoices(invoiceIds, format, options).then((request) => request(axios, basePath));
        },
        /**
         * The invoice whose published PDF is the given document, so a screen showing the document — the \"zichtbaar maken\" dialog in the cliëntdossier — can offer what belongs to the invoice, such as its payment link. 404 when the document is no invoice\'s published PDF.
         * @summary Get the invoice a dossier document publishes
         * @param {string} documentId Document UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getInvoiceByDocument(documentId: string, options?: RawAxiosRequestConfig): AxiosPromise<InvoiceResponse> {
            return localVarFp.getInvoiceByDocument(documentId, options).then((request) => request(axios, basePath));
        },
        /**
         * Retrieves a single invoice by ID with its line items.
         * @summary Get invoice by ID
         * @param {string} invoiceId Invoice UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        getInvoiceById(invoiceId: string, options?: RawAxiosRequestConfig): AxiosPromise<InvoiceDetailResponse> {
            return localVarFp.getInvoiceById(invoiceId, options).then((request) => request(axios, basePath));
        },
        /**
         * Every stored version of this invoice\'s PDF, oldest first. Empty when nothing is stored — an unissued draft, or an invoice issued before its PDF was stored at all.  On the normal path this holds exactly one entry; a second exists only where someone deliberately regenerated. Deliberately unpaged: a version chain is bounded by how often a practice regenerates one nota, not by the size of the organisation.
         * @summary List the stored versions of an invoice PDF
         * @param {string} invoiceId Invoice UUID
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listInvoicePdfVersions(invoiceId: string, options?: RawAxiosRequestConfig): AxiosPromise<Array<InvoiceDocumentVersion>> {
            return localVarFp.listInvoicePdfVersions(invoiceId, options).then((request) => request(axios, basePath));
        },
        /**
         * List invoices with pagination and filtering.
         * @summary List invoices
         * @param {number} [page] Page number (0-indexed)
         * @param {number} [size] Number of items per page
         * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;invoiceDate,desc\&quot;). Supported fields: invoiceDate, dueDate, invoiceNumber, status, totalEuro, paidEuro, createdAt, updatedAt. Any other field falls back to invoiceDate.
         * @param {string} [clientId] Filter by client
         * @param {InvoiceStatus} [status] Filter by status. SENT and OVERDUE are *derived* buckets rather than stored values and are disjoint: OVERDUE matches an issued invoice whose due date has passed (or the rarely stored OVERDUE status), SENT matches an issued invoice that is not yet due. DRAFT, PAID and CANCELLED match the stored status.
         * @param {string} [startDate] Filter by invoice date start
         * @param {string} [endDate] Filter by invoice date end
         * @param {string} [search] Filter by invoice number or client name (partial match, case-insensitive). The client name is matched as it is rendered in the list — first name, tussenvoegsel and last name.
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        listInvoices(page?: number, size?: number, sort?: string, clientId?: string, status?: InvoiceStatus, startDate?: string, endDate?: string, search?: string, options?: RawAxiosRequestConfig): AxiosPromise<InvoiceListResponse> {
            return localVarFp.listInvoices(page, size, sort, clientId, status, startDate, endDate, search, options).then((request) => request(axios, basePath));
        },
        /**
         * Record a payment on an invoice. Only allowed for SENT or OVERDUE invoices.
         * @summary Record payment
         * @param {string} invoiceId Invoice UUID
         * @param {RecordPaymentRequest} recordPaymentRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        recordPayment(invoiceId: string, recordPaymentRequest: RecordPaymentRequest, options?: RawAxiosRequestConfig): AxiosPromise<InvoiceResponse> {
            return localVarFp.recordPayment(invoiceId, recordPaymentRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Render the invoice PDF again from today\'s data and **append** it to the stored document\'s version chain. Requires `billing.update`; the download is `billing.read`, because rendering a new version is a change to the dossier rather than a read of it.  The previous version is never touched. Overwriting the bytes a client received would re-create the very problem storing them solves, so a regeneration adds a version and leaves version 1 — the document the client received — exactly where it is (#3515 Q3). `document` on the invoice response keeps pointing at the version the client already has, which is also what the client portal keeps serving: making a newer version client-facing is a separate, deliberate act, and it is `republishInvoicePdf` (#3986).  Only an issued invoice (SENT/PAID/OVERDUE) can be regenerated, and only one that already has a stored document — there is nothing to append a version to otherwise. A draft is still editable and its preview is rendered on the fly, never stored.  The reason is required and is kept on the new version, so the version list can say why it exists.
         * @summary Regenerate the invoice PDF as a new version
         * @param {string} invoiceId Invoice UUID
         * @param {RegenerateInvoicePdfRequest} regenerateInvoicePdfRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        regenerateInvoicePdf(invoiceId: string, regenerateInvoicePdfRequest: RegenerateInvoicePdfRequest, options?: RawAxiosRequestConfig): AxiosPromise<InvoiceDocumentVersion> {
            return localVarFp.regenerateInvoicePdf(invoiceId, regenerateInvoicePdfRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Make an already stored version of this invoice\'s PDF the one the client sees. Requires `billing.update`.  Regenerating deliberately leaves the client on the version they received (#3967); this is the separate, deliberate act that moves them. Were repointing a side effect of regenerating, a practice re-rendering to check a layout change would silently republish.  The whole of it is moving the invoice\'s `document` reference: the portal resolves the PDF from it and takes no version, so the document it names *is* the version a client sees. Every earlier version keeps its row, its bytes and its reason, stays in `listInvoicePdfVersions`, and stops being client-facing by the same fact that makes the new one client-facing.  `documentId` must name a version of **this invoice\'s own** PDF chain — one of the entries `listInvoicePdfVersions` returns — and anything else is refused. A document id is not authority on its own, and repointing at another invoice\'s nota is the one way this could show a client the wrong bytes.  Portal exposure is not changed here: it is a property of the document and a behandelaar\'s to grant (#3968), so a version that has not been exposed stays unreadable to the client exactly as version 1 would be.
         * @summary Publish a stored version of the invoice PDF to the client
         * @param {string} invoiceId Invoice UUID
         * @param {RepublishInvoicePdfRequest} republishInvoicePdfRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        republishInvoicePdf(invoiceId: string, republishInvoicePdfRequest: RepublishInvoicePdfRequest, options?: RawAxiosRequestConfig): AxiosPromise<InvoiceDocumentVersion> {
            return localVarFp.republishInvoicePdf(invoiceId, republishInvoicePdfRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Set the practice\'s own betaallink (Tikkie, Mollie, a bank request) on an issued invoice, or clear it with null. The cliënt sees it beside the invoice in the portaal and as a Betalen button in the \"staat voor je klaar\" mail. Only an absolute https URL is accepted, and a link can only be set while the invoice awaits payment (SENT or OVERDUE); clearing is always allowed. The change is audit-logged on the invoice.
         * @summary Set or clear the invoice\'s payment link
         * @param {string} invoiceId Invoice UUID
         * @param {UpdateInvoicePaymentLinkRequest} updateInvoicePaymentLinkRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateInvoicePaymentLink(invoiceId: string, updateInvoicePaymentLinkRequest: UpdateInvoicePaymentLinkRequest, options?: RawAxiosRequestConfig): AxiosPromise<InvoiceResponse> {
            return localVarFp.updateInvoicePaymentLink(invoiceId, updateInvoicePaymentLinkRequest, options).then((request) => request(axios, basePath));
        },
        /**
         * Update the status of an invoice. Valid transitions: - DRAFT -> SENT, CANCELLED - SENT -> PAID, OVERDUE, CANCELLED - OVERDUE -> PAID, CANCELLED 
         * @summary Update invoice status
         * @param {string} invoiceId Invoice UUID
         * @param {UpdateInvoiceStatusRequest} updateInvoiceStatusRequest 
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        updateInvoiceStatus(invoiceId: string, updateInvoiceStatusRequest: UpdateInvoiceStatusRequest, options?: RawAxiosRequestConfig): AxiosPromise<InvoiceResponse> {
            return localVarFp.updateInvoiceStatus(invoiceId, updateInvoiceStatusRequest, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * InvoicesApi - object-oriented interface
 */
export class InvoicesApi extends BaseAPI {
    /**
     * Create a new invoice from billing lines. All billing lines must belong to the same client. 
     * @summary Create invoice
     * @param {CreateInvoiceRequest} createInvoiceRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createInvoice(createInvoiceRequest: CreateInvoiceRequest, options?: RawAxiosRequestConfig) {
        return InvoicesApiFp(this.configuration).createInvoice(createInvoiceRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Creates invoices from a set of billing lines, grouping them by client. One invoice is created per client. This is the INVOICE billing method flow where billing lines go directly to invoices (skipping batches).  Validates that all required ZPM fields are present before creating invoices. Returns validation errors if any billing line is missing required data. 
     * @summary Create invoices from billing lines
     * @param {CreateInvoicesFromBillingLinesRequest} createInvoicesFromBillingLinesRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public createInvoicesFromBillingLines(createInvoicesFromBillingLinesRequest: CreateInvoicesFromBillingLinesRequest, options?: RawAxiosRequestConfig) {
        return InvoicesApiFp(this.configuration).createInvoicesFromBillingLines(createInvoicesFromBillingLinesRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Reverse an issued invoice. The original invoice is preserved and moved to CANCELLED (keeping the gapless sequence intact), and a new credit invoice with negative totals is created as a DRAFT, linked back to the original via originalInvoiceId. The credit invoice draws its own gapless number only when it is itself issued (DRAFT -> SENT).  Full credit only (MVP): the whole invoice is reversed. A DRAFT invoice cannot be credited — it never consumed a number and is deleted instead. PAID and CANCELLED invoices are terminal and cannot be credited. 
     * @summary Credit (void) an issued invoice
     * @param {string} invoiceId Invoice UUID to credit
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public creditInvoice(invoiceId: string, options?: RawAxiosRequestConfig) {
        return InvoicesApiFp(this.configuration).creditInvoice(invoiceId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Deletes a draft invoice. Only invoices in DRAFT status can be deleted. 
     * @summary Delete invoice
     * @param {string} invoiceId Invoice UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public deleteInvoice(invoiceId: string, options?: RawAxiosRequestConfig) {
        return InvoicesApiFp(this.configuration).deleteInvoice(invoiceId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Generate a combined PDF containing multiple invoices.  Each invoice contributes its stored document when it has one. An issued invoice with no stored document is rendered now and that render is filed as its document, marked `RECONSTRUCTED` — so the export converges the invoices issued before their PDF was stored at all, without claiming the render is the document the client received. A second export of the same invoice reuses it. A draft carries no invoice number and is never filed.
     * @summary Download batch PDF
     * @param {BatchPdfRequest} batchPdfRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public downloadBatchPdf(batchPdfRequest: BatchPdfRequest, options?: RawAxiosRequestConfig) {
        return InvoicesApiFp(this.configuration).downloadBatchPdf(batchPdfRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Download the invoice as a PDF document.  When the invoice has a stored document (see `document` on the invoice response), those bytes are served unchanged — the document the client received is reproducible, and does not silently change when the client\'s address, the regiebehandelaar, the diagnosis or the template does.  An invoice with no stored document is rendered on the fly from today\'s data. A download never stores what it renders: a read must not create an archival record.
     * @summary Download invoice PDF
     * @param {string} invoiceId Invoice UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public downloadInvoicePdf(invoiceId: string, options?: RawAxiosRequestConfig) {
        return InvoicesApiFp(this.configuration).downloadInvoicePdf(invoiceId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Exports multiple invoices as either a combined PDF or a ZIP archive containing individual PDFs. - format=pdf: Returns a single PDF with all invoices combined - format=zip: Returns a ZIP archive with individual PDF files for each invoice  Both formats serve an invoice\'s stored document when it has one, and file a `RECONSTRUCTED` document for an issued invoice that has none — see `downloadBatchPdf`. 
     * @summary Export multiple invoices
     * @param {string} invoiceIds Comma-separated list of invoice UUIDs
     * @param {ExportInvoicesFormatEnum} format Export format (pdf for combined PDF, zip for individual PDFs in ZIP archive)
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public exportInvoices(invoiceIds: string, format: ExportInvoicesFormatEnum, options?: RawAxiosRequestConfig) {
        return InvoicesApiFp(this.configuration).exportInvoices(invoiceIds, format, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * The invoice whose published PDF is the given document, so a screen showing the document — the \"zichtbaar maken\" dialog in the cliëntdossier — can offer what belongs to the invoice, such as its payment link. 404 when the document is no invoice\'s published PDF.
     * @summary Get the invoice a dossier document publishes
     * @param {string} documentId Document UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getInvoiceByDocument(documentId: string, options?: RawAxiosRequestConfig) {
        return InvoicesApiFp(this.configuration).getInvoiceByDocument(documentId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Retrieves a single invoice by ID with its line items.
     * @summary Get invoice by ID
     * @param {string} invoiceId Invoice UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public getInvoiceById(invoiceId: string, options?: RawAxiosRequestConfig) {
        return InvoicesApiFp(this.configuration).getInvoiceById(invoiceId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Every stored version of this invoice\'s PDF, oldest first. Empty when nothing is stored — an unissued draft, or an invoice issued before its PDF was stored at all.  On the normal path this holds exactly one entry; a second exists only where someone deliberately regenerated. Deliberately unpaged: a version chain is bounded by how often a practice regenerates one nota, not by the size of the organisation.
     * @summary List the stored versions of an invoice PDF
     * @param {string} invoiceId Invoice UUID
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listInvoicePdfVersions(invoiceId: string, options?: RawAxiosRequestConfig) {
        return InvoicesApiFp(this.configuration).listInvoicePdfVersions(invoiceId, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * List invoices with pagination and filtering.
     * @summary List invoices
     * @param {number} [page] Page number (0-indexed)
     * @param {number} [size] Number of items per page
     * @param {string} [sort] Sort specification as \&quot;field,direction\&quot; (e.g., \&quot;invoiceDate,desc\&quot;). Supported fields: invoiceDate, dueDate, invoiceNumber, status, totalEuro, paidEuro, createdAt, updatedAt. Any other field falls back to invoiceDate.
     * @param {string} [clientId] Filter by client
     * @param {InvoiceStatus} [status] Filter by status. SENT and OVERDUE are *derived* buckets rather than stored values and are disjoint: OVERDUE matches an issued invoice whose due date has passed (or the rarely stored OVERDUE status), SENT matches an issued invoice that is not yet due. DRAFT, PAID and CANCELLED match the stored status.
     * @param {string} [startDate] Filter by invoice date start
     * @param {string} [endDate] Filter by invoice date end
     * @param {string} [search] Filter by invoice number or client name (partial match, case-insensitive). The client name is matched as it is rendered in the list — first name, tussenvoegsel and last name.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public listInvoices(page?: number, size?: number, sort?: string, clientId?: string, status?: InvoiceStatus, startDate?: string, endDate?: string, search?: string, options?: RawAxiosRequestConfig) {
        return InvoicesApiFp(this.configuration).listInvoices(page, size, sort, clientId, status, startDate, endDate, search, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Record a payment on an invoice. Only allowed for SENT or OVERDUE invoices.
     * @summary Record payment
     * @param {string} invoiceId Invoice UUID
     * @param {RecordPaymentRequest} recordPaymentRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public recordPayment(invoiceId: string, recordPaymentRequest: RecordPaymentRequest, options?: RawAxiosRequestConfig) {
        return InvoicesApiFp(this.configuration).recordPayment(invoiceId, recordPaymentRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Render the invoice PDF again from today\'s data and **append** it to the stored document\'s version chain. Requires `billing.update`; the download is `billing.read`, because rendering a new version is a change to the dossier rather than a read of it.  The previous version is never touched. Overwriting the bytes a client received would re-create the very problem storing them solves, so a regeneration adds a version and leaves version 1 — the document the client received — exactly where it is (#3515 Q3). `document` on the invoice response keeps pointing at the version the client already has, which is also what the client portal keeps serving: making a newer version client-facing is a separate, deliberate act, and it is `republishInvoicePdf` (#3986).  Only an issued invoice (SENT/PAID/OVERDUE) can be regenerated, and only one that already has a stored document — there is nothing to append a version to otherwise. A draft is still editable and its preview is rendered on the fly, never stored.  The reason is required and is kept on the new version, so the version list can say why it exists.
     * @summary Regenerate the invoice PDF as a new version
     * @param {string} invoiceId Invoice UUID
     * @param {RegenerateInvoicePdfRequest} regenerateInvoicePdfRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public regenerateInvoicePdf(invoiceId: string, regenerateInvoicePdfRequest: RegenerateInvoicePdfRequest, options?: RawAxiosRequestConfig) {
        return InvoicesApiFp(this.configuration).regenerateInvoicePdf(invoiceId, regenerateInvoicePdfRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Make an already stored version of this invoice\'s PDF the one the client sees. Requires `billing.update`.  Regenerating deliberately leaves the client on the version they received (#3967); this is the separate, deliberate act that moves them. Were repointing a side effect of regenerating, a practice re-rendering to check a layout change would silently republish.  The whole of it is moving the invoice\'s `document` reference: the portal resolves the PDF from it and takes no version, so the document it names *is* the version a client sees. Every earlier version keeps its row, its bytes and its reason, stays in `listInvoicePdfVersions`, and stops being client-facing by the same fact that makes the new one client-facing.  `documentId` must name a version of **this invoice\'s own** PDF chain — one of the entries `listInvoicePdfVersions` returns — and anything else is refused. A document id is not authority on its own, and repointing at another invoice\'s nota is the one way this could show a client the wrong bytes.  Portal exposure is not changed here: it is a property of the document and a behandelaar\'s to grant (#3968), so a version that has not been exposed stays unreadable to the client exactly as version 1 would be.
     * @summary Publish a stored version of the invoice PDF to the client
     * @param {string} invoiceId Invoice UUID
     * @param {RepublishInvoicePdfRequest} republishInvoicePdfRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public republishInvoicePdf(invoiceId: string, republishInvoicePdfRequest: RepublishInvoicePdfRequest, options?: RawAxiosRequestConfig) {
        return InvoicesApiFp(this.configuration).republishInvoicePdf(invoiceId, republishInvoicePdfRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Set the practice\'s own betaallink (Tikkie, Mollie, a bank request) on an issued invoice, or clear it with null. The cliënt sees it beside the invoice in the portaal and as a Betalen button in the \"staat voor je klaar\" mail. Only an absolute https URL is accepted, and a link can only be set while the invoice awaits payment (SENT or OVERDUE); clearing is always allowed. The change is audit-logged on the invoice.
     * @summary Set or clear the invoice\'s payment link
     * @param {string} invoiceId Invoice UUID
     * @param {UpdateInvoicePaymentLinkRequest} updateInvoicePaymentLinkRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateInvoicePaymentLink(invoiceId: string, updateInvoicePaymentLinkRequest: UpdateInvoicePaymentLinkRequest, options?: RawAxiosRequestConfig) {
        return InvoicesApiFp(this.configuration).updateInvoicePaymentLink(invoiceId, updateInvoicePaymentLinkRequest, options).then((request) => request(this.axios, this.basePath));
    }

    /**
     * Update the status of an invoice. Valid transitions: - DRAFT -> SENT, CANCELLED - SENT -> PAID, OVERDUE, CANCELLED - OVERDUE -> PAID, CANCELLED 
     * @summary Update invoice status
     * @param {string} invoiceId Invoice UUID
     * @param {UpdateInvoiceStatusRequest} updateInvoiceStatusRequest 
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    public updateInvoiceStatus(invoiceId: string, updateInvoiceStatusRequest: UpdateInvoiceStatusRequest, options?: RawAxiosRequestConfig) {
        return InvoicesApiFp(this.configuration).updateInvoiceStatus(invoiceId, updateInvoiceStatusRequest, options).then((request) => request(this.axios, this.basePath));
    }
}

export const ExportInvoicesFormatEnum = {
    Pdf: 'pdf',
    Zip: 'zip'
} as const;
export type ExportInvoicesFormatEnum = typeof ExportInvoicesFormatEnum[keyof typeof ExportInvoicesFormatEnum];


